import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import type {
	CommandPaletteResult,
	CommandPaletteSource,
} from '../command-palette/types';

/**
 * Turns the report keys of the Recent list into rows (PD-14959). Reports are
 * catalogued in the reporting module, which this library cannot import, so
 * the app hands the resolver in. A key the user can no longer open resolves
 * to nothing and is left out.
 */
export interface GlobalSearchReportRecentsModel {
	/** A row per key the user can open now, by report key. */
	resolve: (reportKeys: ReadonlyArray<string>) => ReadonlyMap<string, CommandPaletteResult>;
	/** Called when Recent shows report keys, so the resolver can load what it needs. */
	prepare?: (reportKeys: ReadonlyArray<string>) => void;
	/** The resolver's data changed (e.g. custom reports arrived); returns the unsubscribe. */
	subscribe?: (onChange: () => void) => () => void;
}

interface GlobalSearchContextValue {
	/** The "Go to" sources (pages, settings, reports, the guide), sorted by `order`. */
	sources: ReadonlyArray<CommandPaletteSource>;
	/** Add a source for the life of the caller; returns the remover. */
	registerSource: (source: CommandPaletteSource) => () => void;
	/**
	 * Bumped each time the shortcut is pressed. The header search box (or, on
	 * a narrow screen, the search sheet) takes focus on every change.
	 */
	focusRequest: number;
	requestFocus: () => void;
	/**
	 * Whether the full-screen search sheet (phones and narrow windows) is open.
	 * Held here rather than in the button that opens it, because the phone's
	 * bottom tab bar hides itself while the soft keyboard is up: a sheet owned
	 * by a button in that bar would close the moment its own input took focus.
	 */
	sheetOpen: boolean;
	openSheet: () => void;
	closeSheet: () => void;
	reportRecents?: GlobalSearchReportRecentsModel;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(null);

/** Cmd+K on a Mac, Ctrl+K elsewhere. */
export function isGlobalSearchShortcut(event: KeyboardEvent): boolean {
	if (event.key.toLowerCase() !== 'k' || event.altKey || event.shiftKey) {
		return false;
	}
	const isMac = /mac|iphone|ipad/i.test(navigator.userAgent);
	return isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

/** The shortcut as the box shows it. */
export function globalSearchShortcutLabel(): string {
	return /mac|iphone|ipad/i.test(navigator.userAgent) ? '⌘K' : 'Ctrl K';
}

interface GlobalSearchProviderProps {
	/** Sources available from mount; modules may add more via `registerSource`. */
	sources?: ReadonlyArray<CommandPaletteSource>;
	reportRecents?: GlobalSearchReportRecentsModel;
	children: React.ReactNode;
}

/**
 * PD-14959 — owns global search's Go to sources (PD-16044's settings,
 * navigation, reports and guide sources, which fed the Cmd+K palette this
 * replaced) and the keyboard shortcut. The shortcut no longer opens a modal:
 * it only asks the persistent header search box for focus. It is a modifier
 * combination, so it works from inside a text field too; a page that needs
 * Cmd+K for itself calls `preventDefault` first and this listener stands down.
 */
export function GlobalSearchProvider({
	sources: initialSources = [],
	reportRecents,
	children,
}: GlobalSearchProviderProps): React.ReactElement {
	const [focusRequest, setFocusRequest] = useState(0);
	const [sheetOpen, setSheetOpen] = useState(false);
	const [registered, setRegistered] = useState<ReadonlyArray<CommandPaletteSource>>([]);

	const requestFocus = useCallback((): void => {
		setFocusRequest((previous) => previous + 1);
	}, []);

	const openSheet = useCallback((): void => {
		setSheetOpen(true);
	}, []);

	const closeSheet = useCallback((): void => {
		setSheetOpen(false);
	}, []);

	const registerSource = useCallback((source: CommandPaletteSource): (() => void) => {
		setRegistered((previous) => [...previous.filter((existing) => existing.id !== source.id), source]);
		return (): void => {
			setRegistered((previous) => previous.filter((existing) => existing !== source));
		};
	}, []);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.defaultPrevented || !isGlobalSearchShortcut(event)) {
				return;
			}
			event.preventDefault();
			requestFocus();
		};
		window.addEventListener('keydown', onKeyDown);
		return (): void => {
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [requestFocus]);

	const sources = useMemo(
		() => [...initialSources, ...registered].sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
		[initialSources, registered],
	);

	const value = useMemo<GlobalSearchContextValue>(
		() => ({
			sources,
			registerSource,
			focusRequest,
			requestFocus,
			sheetOpen,
			openSheet,
			closeSheet,
			reportRecents,
		}),
		[sources, registerSource, focusRequest, requestFocus, sheetOpen, openSheet, closeSheet, reportRecents],
	);

	return <GlobalSearchContext.Provider value={value}>{children}</GlobalSearchContext.Provider>;
}

export function useGlobalSearch(): GlobalSearchContextValue {
	const context = useContext(GlobalSearchContext);
	if (context == null) {
		throw new Error('useGlobalSearch must be used inside a GlobalSearchProvider');
	}
	return context;
}

/** Null-safe variant for chrome that may render outside the provider (the storefront, the config app). */
export function useOptionalGlobalSearch(): GlobalSearchContextValue | null {
	return useContext(GlobalSearchContext);
}
